import BaseDataObject from './BaseDataObject';
import BaseReduction from './BaseReduction';

export default class PointReduction extends BaseReduction {
  constructor() {
    super(...arguments);
    this.ensureArray('pointSums');
    this.ensureArray('factorOnPointSum');
    this.ensureArray('winnersInIntervals');
    this.ensureArray('misc');
  }

  isEmpty() {
    let num, n;

    num = this.getNumberOfPointSums();
    for (n = 0; n < num; n++) {
      if (!this.getPointSum(n).isEmpty()) {
        return false;
      }
    }

    num = this.getNumberOfFactorsOnPointSums();
    for (n = 0; n < num; n++) {
      if (!this.getFactorOnPointSum(n).isEmpty()) {
        return false;
      }
    }

    num = this.getNumberOfWinnersInIntervals();
    for (n = 0; n < num; n++) {
      if (!this.getWinnersInInterval(n).isEmpty()) {
        return false;
      }
    }

    num = this.getNumberOfMiscs();
    for (n = 0; n < num; n++) {
      if (!this.getMisc(n).isEmpty()) {
        return false;
      }
    }

    return true;
  }

  getNumberOfPointSums() {
    return this.getNumberOf('pointSums');
  }

  getPointSum(index) {
    return this.getObject('pointSums', PointReductionPointSum, index);
  }

  getPointSums() {
    return this.getObjects('pointSums', PointReductionPointSum);
  }

  addPointSum() {
    this.invalidate();
    return this.addObject('pointSums', PointReductionPointSum);
  }

  deletePointSum(index) {
    this.invalidate();
    return this.deleteObject('pointSums', index);
  }

  getNumberOfFactorsOnPointSums() {
    return this.getNumberOf('factorOnPointSum');
  }

  getFactorOnPointSum(index) {
    return this.getObject(
      'factorOnPointSum',
      PointReductionFactorOnPointSum,
      index,
    );
  }

  getFactorsOnPointSums() {
    return this.getObjects('factorOnPointSum', PointReductionFactorOnPointSum);
  }

  addFactorOnPointSum() {
    this.invalidate();
    return this.addObject('factorOnPointSum', PointReductionFactorOnPointSum);
  }

  deleteFactorOnPointSum(index) {
    this.invalidate();
    return this.deleteObject('factorOnPointSum', index);
  }

  getNumberOfWinnersInIntervals() {
    return this.getNumberOf('winnersInIntervals');
  }

  getWinnersInInterval(index) {
    return this.getObject(
      'winnersInIntervals',
      PointReductionWinnersInInterval,
      index,
    );
  }

  getWinnersInIntervals() {
    return this.getObjects(
      'winnersInIntervals',
      PointReductionWinnersInInterval,
    );
  }

  addWinnersInInterval() {
    this.invalidate();
    return this.addObject(
      'winnersInIntervals',
      PointReductionWinnersInInterval,
    );
  }

  deleteWinnersInInterval(index) {
    this.invalidate();
    return this.deleteObject('winnersInIntervals', index);
  }

  getNumberOfMiscs() {
    return this.getNumberOf('misc');
  }

  getMisc(index) {
    return this.getObject('misc', PointReductionMisc, index);
  }

  getMiscs() {
    return this.getObjects('misc', PointReductionMisc);
  }

  addMisc() {
    this.invalidate();
    return this.addObject('misc', PointReductionMisc);
  }

  deleteMisc(index) {
    this.invalidate();
    return this.deleteObject('misc', index);
  }
}

class PointReductionPointSum extends BaseDataObject {
  constructor() {
    super(...arguments);
    this.ensureArray('legNumbers');
    this.ensureInteger('minPoints', -1);
    this.ensureInteger('maxPoints', -1);
  }

  isEmpty() {
    if (this.getMinPoints() === -1 && this.getMaxPoints() === -1) {
      return true;
    } else if (!this.getNumberOfRaces()) {
      return true;
    } else if (this.getLegNumbers().length === 0) {
      return true;
    } else {
      return false;
    }
  }

  getMinPoints() {
    return this.getInteger('minPoints', -1);
  }
  setMinPoints(v) {
    this.invalidate();
    return this.setInteger('minPoints', v);
  }

  getMaxPoints() {
    return this.getInteger('maxPoints', -1);
  }
  setMaxPoints(v) {
    this.invalidate();
    return this.setInteger('maxPoints', v);
  }

  getNumberOfRaces() {
    return this.getInteger('numberOfRaces');
  }
  setNumberOfRaces(v) {
    this.invalidate();
    return this.setInteger('numberOfRaces', v);
  }

  getLegNumbers() {
    return this.getIntegerArray('legNumbers');
  }
  setLegNumbers(v) {
    this.invalidate();
    return this.setIntegerArray('legNumbers', v);
  }
}

class PointReductionFactorOnPointSum extends BaseDataObject {
  constructor() {
    super(...arguments);
    this.ensureInteger('factor', 1);
    this.ensureInteger('minPoints', -1);
    this.ensureInteger('maxPoints', -1);
  }

  isEmpty() {
    return (
      this.getMinPoints() === -1 &&
      this.getMaxPoints() === -1 &&
      this.getFactor() === 1
    );
  }

  getMinPoints() {
    return this.getInteger('minPoints', -1);
  }
  setMinPoints(v) {
    this.invalidate();
    return this.setInteger('minPoints', v);
  }

  getMaxPoints() {
    return this.getInteger('maxPoints', 1);
  }
  setMaxPoints(v) {
    this.invalidate();
    return this.setInteger('maxPoints', v);
  }

  getFactor() {
    return this.getInteger('factor');
  }
  setFactor(v) {
    // Anything falsy must be transformed to 1. Factor 0 not allowed.
    const ensuredFactorValue = Math.max(v, 1);
    this.invalidate();
    return this.setInteger('factor', ensuredFactorValue);
  }
}

class PointReductionWinnersInInterval extends BaseDataObject {
  constructor() {
    super(...arguments);
    this.ensureArray('legNumbers');
    this.ensureInteger('fromPoints', -1);
    this.ensureInteger('toPoints', -1);
    this.ensureInteger('minNumberOfRaces', -1);
    this.ensureInteger('maxNumberOfRaces', -1);
  }

  isEmpty() {
    if (this.getFromPoints() === -1 && this.getToPoints() === -1) {
      return true;
    } else if (
      this.getMinNumberOfRaces() === -1 &&
      this.getMaxNumberOfRaces() === -1
    ) {
      return true;
    } else if (this.getLegNumbers().length === 0) {
      return true;
    } else {
      return false;
    }
  }

  getFromPoints() {
    return this.getInteger('fromPoints', -1);
  }
  setFromPoints(v) {
    this.invalidate();
    return this.setInteger('fromPoints', v);
  }

  getToPoints() {
    return this.getInteger('toPoints', -1);
  }
  setToPoints(v) {
    this.invalidate();
    return this.setInteger('toPoints', v);
  }

  getMinNumberOfRaces() {
    return this.getInteger('minNumberOfRaces', -1);
  }
  setMinNumberOfRaces(v) {
    this.invalidate();
    return this.setInteger('minNumberOfRaces', v);
  }

  getMaxNumberOfRaces() {
    return this.getInteger('maxNumberOfRaces', -1);
  }
  setMaxNumberOfRaces(v) {
    this.invalidate();
    return this.setInteger('maxNumberOfRaces', v);
  }

  getLegNumbers() {
    return this.getIntegerArray('legNumbers');
  }
  setLegNumbers(v) {
    this.invalidate();
    return this.setIntegerArray('legNumbers', v);
  }
}

class PointReductionMisc extends BaseDataObject {
  constructor() {
    super(...arguments);
    this.ensureArray('legNumbers');
    this.ensureInteger('fromPoints', -1);
    this.ensureInteger('toPoints', -1);
  }

  isEmpty() {
    if (!this.getRequireMode()) {
      return true;
    } else if (!this.getType()) {
      return true;
    } else if (this.getLegNumbers().length === 0) {
      return true;
    } else {
      return false;
    }
  }

  getRequireMode() {
    return this.getString('requireMode');
  }
  setRequireMode(v) {
    this.invalidate();
    return this.setString('requireMode', v);
  }

  getType() {
    return this.getString('type');
  }
  setType(v) {
    this.invalidate();
    return this.setString('type', v);
  }

  getFromPoints() {
    return this.getInteger('fromPoints', -1);
  }
  setFromPoints(v) {
    this.invalidate();
    return this.setInteger('fromPoints', v);
  }

  getToPoints() {
    return this.getInteger('toPoints', -1);
  }
  setToPoints(v) {
    this.invalidate();
    return this.setInteger('toPoints', v);
  }

  getLegNumbers() {
    return this.getIntegerArray('legNumbers');
  }
  setLegNumbers(v) {
    this.invalidate();
    return this.setIntegerArray('legNumbers', v);
  }
}
